import React, { useEffect, useMemo, useState } from 'react';

const DAY_MS = 86400000;
const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日'];
const toOrdinal = value => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const ordinal = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(ordinal).toISOString().slice(0, 10) === value ? ordinal : null;
};
const fromOrdinal = ordinal => new Date(ordinal).toISOString().slice(0, 10);
const monthOrdinal = month => toOrdinal(`${month}-01`);
const shiftMonth = (month, offset) => {
  const date = new Date(monthOrdinal(month));
  date.setUTCMonth(date.getUTCMonth() + offset);
  return date.toISOString().slice(0, 7);
};

export default function ReadingPlanCheckInCalendar({
  startDate,
  days,
  selectedDayIndex,
  completedDays,
  language,
  translate,
  onClose,
  onSelectDay,
}) {
  const startOrdinal = toOrdinal(startDate);
  const indexedDays = useMemo(() => (days || []).map((day, index) => ({ day, index })), [days]);
  const selectedNumber = Number(indexedDays[selectedDayIndex]?.day?.dayNumber) || 1;
  const selectedMonth = startOrdinal === null
    ? ''
    : fromOrdinal(startOrdinal + (selectedNumber - 1) * DAY_MS).slice(0, 7);
  const [month, setMonth] = useState(selectedMonth);

  useEffect(() => {
    setMonth(selectedMonth);
  }, [selectedMonth]);

  const lastNumber = Math.max(1, ...indexedDays.map(({ day }) => Number(day.dayNumber) || 0));
  const firstMonth = startDate.slice(0, 7);
  const lastMonth = startOrdinal === null
    ? firstMonth
    : fromOrdinal(startOrdinal + (lastNumber - 1) * DAY_MS).slice(0, 7);
  const currentMonth = month || selectedMonth;
  const today = new Date();
  const todayOrdinal = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = useMemo(() => {
    const first = monthOrdinal(currentMonth);
    if (first === null) return [];
    const firstDay = new Date(first);
    const offset = (firstDay.getUTCDay() + 6) % 7;
    const numberOfDays = new Date(Date.UTC(firstDay.getUTCFullYear(), firstDay.getUTCMonth() + 1, 0)).getUTCDate();
    const byOrdinal = new Map(indexedDays.map(({ day, index }) => [
      startOrdinal + ((Number(day.dayNumber) || index + 1) - 1) * DAY_MS,
      { day, index },
    ]));
    const result = Array(offset).fill(null);
    for (let dayOfMonth = 1; dayOfMonth <= numberOfDays; dayOfMonth += 1) {
      const ordinal = first + (dayOfMonth - 1) * DAY_MS;
      result.push({ dayOfMonth, ordinal, planDay: byOrdinal.get(ordinal) });
    }
    while (result.length % 7) result.push(null);
    return result;
  }, [currentMonth, indexedDays, startOrdinal]);

  if (startOrdinal === null || !indexedDays.length) return null;

  const monthLabel = new Intl.DateTimeFormat(language === 'S' ? 'zh-CN' : 'zh-TW', {
    year: 'numeric', month: 'long', timeZone: 'UTC',
  }).format(new Date(monthOrdinal(currentMonth)));

  return (
    <section className="reading-plan-checkin-calendar" aria-label={translate('我的打卡日曆')}>
      <div className="reading-plan-checkin-calendar-header">
        <span aria-hidden="true" className="reading-plan-checkin-calendar-icon">▦</span>
        <strong>{translate('我的打卡日曆')}</strong>
        <span className="reading-plan-checkin-calendar-count">{completedDays || 0}/{indexedDays.length}</span>
        <button type="button" onClick={onClose} aria-label={translate('收起打卡日曆')}>×</button>
      </div>
        <div className="reading-plan-checkin-calendar-content">
          <div className="reading-plan-checkin-calendar-month">
            <button type="button" disabled={currentMonth <= firstMonth} onClick={() => setMonth(shiftMonth(currentMonth, -1))} aria-label={translate('上個月')}>‹</button>
            <strong aria-live="polite">{monthLabel}</strong>
            <button type="button" disabled={currentMonth >= lastMonth} onClick={() => setMonth(shiftMonth(currentMonth, 1))} aria-label={translate('下個月')}>›</button>
          </div>
          <div className="reading-plan-checkin-calendar-grid" role="group" aria-label={monthLabel}>
            {WEEKDAYS.map(weekday => <span className="reading-plan-checkin-calendar-weekday" key={weekday}>{translate(`週${weekday}`)}</span>)}
            {cells.map((cell, index) => {
              if (!cell) return <span className="reading-plan-checkin-calendar-blank" key={`blank-${index}`} />;
              const { dayOfMonth, ordinal, planDay } = cell;
              if (!planDay) return <span className="reading-plan-checkin-calendar-outside" key={ordinal}>{dayOfMonth}</span>;
              const { day, index: dayIndex } = planDay;
              const status = day.completed ? '已打卡' : (ordinal < todayOrdinal ? '未打卡' : '未到日期');
              return (
                <button
                  key={ordinal}
                  type="button"
                  className={`reading-plan-checkin-calendar-day${day.completed ? ' is-completed' : ''}${ordinal < todayOrdinal && !day.completed ? ' is-missed' : ''}${ordinal === todayOrdinal ? ' is-today' : ''}${dayIndex === selectedDayIndex ? ' is-selected' : ''}`}
                  aria-label={`${fromOrdinal(ordinal)} · ${translate('第')} ${day.dayNumber} ${translate('天')} · ${translate(status)}`}
                  aria-current={ordinal === todayOrdinal ? 'date' : undefined}
                  onClick={() => onSelectDay(dayIndex)}
                >
                  <span>{dayOfMonth}</span>
                  <small>{day.completed ? '✓' : (ordinal < todayOrdinal ? '·' : '')}</small>
                </button>
              );
            })}
          </div>
          <p className="reading-plan-checkin-calendar-note">
            <span><i className="is-completed" />{translate('已打卡')}</span>
            <span><i className="is-missed" />{translate('未打卡')}</span>
            <span>{translate('按計劃日期排列；點日期查看當天內容。')}</span>
          </p>
        </div>
    </section>
  );
}
